import { CoreCompany, CoreViolationEntry, doesEntityAllowUpdate, isValueDefined } from "@mm-products/java-multima-framework";
import { Theme } from "@mui/material/styles";
import { SystemStyleObject } from "@mui/system";
import dayjs, { Dayjs } from "dayjs";
import { Path, PathValue } from "react-hook-form";
import { toast } from "react-toastify";
import { readOnlyInputSx, toastStyles } from "../../../TaxGuruStyles";
import { TaxDocumentDto, TaxDocumentRequest, TaxVatRateDto } from "../../../backend/taxguru";
import {
    validateDocumentBeforeConfirm,
    validateDocumentBeforeFinishCorrection,
    validateDocumentBeforeSave,
} from "../../../backend/taxguru/client/taxDocumentClient";
import { listTaxVatRates } from "../../../backend/taxguru/client/taxVatRateClient";
import { RecogniserResponse } from "../../../backend/taxguru/models/RecogniserResponse";
import { TaxCentreDto } from "../../../backend/taxguru/models/TaxCentreDto";
import i18n from "../../../i18n";
import { setCoreValidationResponse, ValidationResult } from "../../../redux/documentSlice";
import { store } from "../../../redux/general/reduxStore";
import { dateForTaxWithTime, isDateObject, isDateObjectValid, newDateObjectInstance, toDateObject } from "../../../utils/dateObjectUtils";
import { toDateObjectOrNull } from "../../../utils/objectUtils";
import { DocumentDetailFormTypes } from "../detail/DocumentDetail.types";
import { RecogniserMapping } from "../recogniserFieldMapping";
import { SelectedAttachment, UploadMode } from "../upload/useDocumentUpload";
import { changeDocumentStateWrap, confirmDocumentWrap, createDocumentRequest, saveExtractedDocumentWrap } from "./documentApiUtils";
import { VatRatesType } from "./hook/useVatPayer";
import { createRecogniserCorrectionOnSubmit } from "./recogniserUtils";
import rateType = TaxVatRateDto.rateType;
import errorType = CoreViolationEntry.errorType;
import status = TaxDocumentDto.status;

export type DateObjectInput = Dayjs | string | null | undefined;
export type FindVatRatesInput = TaxDocumentDto | Array<DateObjectInput> | DocumentDetailFormTypes;
export type DocumentUpdateResult = {
    reloadDocument: boolean;
    violations: Array<CoreViolationEntry>;
    savedDocument?: TaxDocumentDto;
    taxDocumentRequest: TaxDocumentRequest;
};
export type DocumentUpdateAction = "confirm" | "save" | "finishCorrection";

export const isDocNameRegex = /\.isdoc/i;

export const isRecogniserCorrectionUpdateSupported = (document: TaxDocumentDto) => document.status === status.UNDER_CORRECTION;

export function isDocumentReadOnly(document: TaxDocumentDto) {
    return !doesEntityAllowUpdate(document) || (document.status !== status.EXTRACTED && document.status !== status.UNDER_CORRECTION);
}

function handleValidationErrors(backendValidationMessages: Array<CoreViolationEntry>, documentFormData: DocumentDetailFormTypes) {
    const validationResult = createValidationResult(backendValidationMessages, documentFormData);
    store.dispatch(setCoreValidationResponse(validationResult));
}

export function createDefaultDocumentValues(taxDocument: TaxDocumentDto): DocumentDetailFormTypes {
    return {
        type: taxDocument?.type ?? "",
        kind: taxDocument?.kind ?? "",
        ownerNote: taxDocument?.ownerNote ?? "",
        processorNote: taxDocument?.processorNote ?? "",
        description: taxDocument?.description ?? "",
        regNumber: taxDocument?.regNumber ?? "",
        customerCompanyIdNumber: taxDocument.customerCompanyIdNumber ?? "",
        supplierCompanyIdNumber: taxDocument.supplierCompanyIdNumber ?? "",
        owner: taxDocument?.owner,
        currency: taxDocument.currency,

        supplier: taxDocument.supplier,
        supplierPerson: taxDocument.supplierPerson,
        supplierTaxIdNumber: taxDocument.supplierTaxIdNumber,
        paymentMeans: taxDocument.paymentMeans ?? "",
        amountTotal: taxDocument.amountTotal ?? "",
        payableAmount: taxDocument.payableAmount ?? "",
        baseTotal: taxDocument.baseTotal ?? "",
        vatTotal: taxDocument.vatTotal ?? "",
        baseVatBasic: taxDocument.baseVatBasic ?? "",
        baseVatReduced: taxDocument.baseVatReduced ?? "",
        baseVatReduced2: taxDocument.baseVatReduced2 ?? "",
        baseVat0: taxDocument.baseVat0 ?? "",
        baseVatNone: taxDocument.baseVatNone ?? "",
        baseVatUnknown: taxDocument.baseVatUnknown ?? "",
        vatBasic: taxDocument.vatBasic ?? "",
        vatReduced: taxDocument.vatReduced ?? "",
        vatReduced2: taxDocument.vatReduced2 ?? "",
        vatUnknown: taxDocument.vatUnknown ?? "",
        amountVatBasic: taxDocument.amountVatBasic ?? "",
        amountVatReduced: taxDocument.amountVatReduced ?? "",
        amountVatReduced2: taxDocument.amountVatReduced2 ?? "",
        amountVatUnknown: taxDocument.amountVatUnknown ?? "",
        classification: taxDocument.classification ?? "",
        abraDocumentType: taxDocument.abraDocumentType,

        taxableSupplyDate: toDateObjectOrNull(taxDocument.taxableSupplyDate),
        vatApplicationDate: toDateObjectOrNull(taxDocument.vatApplicationDate),
        issueDate: toDateObjectOrNull(taxDocument.issueDate),
        dueDate: toDateObjectOrNull(taxDocument.dueDate),

        preAccounting: taxDocument.preAccounting,

        centre: taxDocument.centre,
        activity: taxDocument.activity,
        contract: taxDocument.contract,
        recogniserSupplierName: taxDocument.recogniserSupplierName ?? "",
        recogniserSupplierAddress: taxDocument.recogniserSupplierAddress ?? "",
        reverseCharge: taxDocument.reverseCharge ?? false,
        vatApplicable: taxDocument.vatApplicable ?? false,
        variableSymbol: taxDocument.variableSymbol ?? "",
        accountNumber: taxDocument.accountNumber ?? "",
        bankCode: taxDocument.bankCode ?? "",
        iban: taxDocument.iban ?? "",
        bic: taxDocument.bic ?? "",
    };
}

export async function performUpdateAction(
    action: DocumentUpdateAction,
    taxDocument: TaxDocumentDto,
    formData: DocumentDetailFormTypes,
    recogniserLearningResponse: RecogniserResponse,
    mappings: Array<RecogniserMapping>
) {
    if (!taxDocument.id) {
        throw new Error("Empty document id provided in 'performUpdateAction'");
    }
    console.debug("Before update action: " + action);
    const recogniserCorrection = createRecogniserCorrectionOnSubmit(taxDocument, formData, recogniserLearningResponse, mappings);
    if (action === "save") {
        const taxDocumentRequest = createDocumentRequest(taxDocument, recogniserCorrection, formData);
        return await onDocumentSave(taxDocument.id, taxDocumentRequest, formData);
    } else if (action === "confirm") {
        const taxDocumentRequest = createDocumentRequest(taxDocument, recogniserCorrection, formData);
        return await onDocumentConfirm(taxDocument.id, taxDocumentRequest, formData);
    } else {
        const taxDocumentRequest = createDocumentRequest(taxDocument, recogniserCorrection, formData);
        return await onDocumentFinishCorrection(taxDocument.id, taxDocumentRequest, formData);
    }
}

async function onDocumentSave(
    taxDocumentId: string,
    taxDocumentRequest: TaxDocumentRequest,
    formData: DocumentDetailFormTypes
): Promise<DocumentUpdateResult> {
    return await validateDocumentBeforeSave(taxDocumentId, taxDocumentRequest).then(async ({ data }) => {
        handleValidationErrors(data.errors ?? [], formData);
        const savedDocument = await saveExtractedDocumentWrap(taxDocumentId, taxDocumentRequest);
        return {
            reloadDocument: false,
            savedDocument,
            taxDocumentRequest,
            violations: [],
        };
    });
}

async function onDocumentConfirm(
    taxDocumentId: string,
    taxDocumentRequest: TaxDocumentRequest,
    formData: DocumentDetailFormTypes
): Promise<DocumentUpdateResult> {
    return await validateDocumentBeforeConfirm(taxDocumentId, taxDocumentRequest).then(async ({ data }) => {
        const isValid = (data?.errors?.length ?? 0) === 0;
        if (isValid) {
            await confirmDocumentWrap(taxDocumentId, taxDocumentRequest);
        } else {
            handleValidationErrors(data.errors ?? [], formData);
        }
        return {
            reloadDocument: isValid,
            violations: data?.errors ?? [],
            taxDocumentRequest,
        };
    });
}

async function onDocumentFinishCorrection(
    taxDocumentId: string,
    taxDocumentRequest: TaxDocumentRequest,
    formData: DocumentDetailFormTypes
): Promise<DocumentUpdateResult> {
    return await validateDocumentBeforeFinishCorrection(taxDocumentId, taxDocumentRequest).then(async ({ data }) => {
        const isValid = (data?.errors?.length ?? 0) === 0;
        if (isValid) {
            await changeDocumentStateWrap(taxDocumentId, status.EXTRACTED);
        } else {
            handleValidationErrors(data.errors ?? [], formData);
        }
        return {
            reloadDocument: isValid,
            violations: data?.errors ?? [],
            taxDocumentRequest,
        };
    });
}

export function resolveSupplierCompanyLabel(company: CoreCompany | undefined) {
    return company?.name ?? "N/A";
}

export function resolveCentreNameLabel(centre: TaxCentreDto | undefined) {
    return `${centre?.name ?? "N/A"}`;
}

/**
 * @deprecated
 */
export function synchroniseDocumentDates(
    source: "taxableSupplyDate" | "issueDate" | "dueDate",
    value: Dayjs | null | undefined,
    setValue: (field: Path<DocumentDetailFormTypes>, value: Dayjs | null | undefined) => void
) {
    if (source === "taxableSupplyDate") {
        setValue("issueDate", value);
        setValue("dueDate", value);
    } else if (source === "issueDate") {
        setValue("taxableSupplyDate", value);
        setValue("dueDate", value);
    } else if (source === "dueDate") {
        setValue("taxableSupplyDate", value);
        setValue("issueDate", value);
    }
}

export function checkUploadedFiles(attachmentState: Array<SelectedAttachment>, uploadMode: UploadMode): boolean {
    if (uploadMode === "single-document") {
        const imagesCount = attachmentState.filter((item) => item.file.type.startsWith("image")).length;
        const pdfCount = attachmentState.filter((item) => item.file.type.startsWith("application/pdf")).length;
        const isDocCount = attachmentState.filter((item) => isDocNameRegex.test(item.file.name)).length;
        const mixedImagesState = imagesCount !== 0 && imagesCount !== attachmentState.length;
        const otherMixed = pdfCount + isDocCount > 1;
        if (mixedImagesState || otherMixed) {
            toast.error(i18n.t("document.validation.documentUploadMixedAttachmentsSingleMode"), { style: toastStyles.error });
            return false;
        }
    }
    return true;
}

export function findMomentForVatRates(input: FindVatRatesInput) {
    if (Array.isArray(input)) {
        return dateForVatRates(...input);
    } else {
        return dateForVatRates(input.issueDate, input.taxableSupplyDate, input.dueDate, input.vatApplicationDate);
    }
}

export function findAndSetVatRates(dateDateObject: Dayjs, onVatRates: (vatRates: VatRatesType | undefined) => void) {
    const formattedDate = dateDateObject.format("YYYY-MM-DD");
    listTaxVatRates({
        specificFilters: [{ name: "validDate", value: formattedDate }],
    })
        .then(({ data }) => {
            let vat0: TaxVatRateDto | undefined;
            let vatNone: TaxVatRateDto | undefined;
            let vatBasic: TaxVatRateDto | undefined;
            let vatReduced: TaxVatRateDto | undefined;
            let vatReduced2: TaxVatRateDto | undefined;
            let vatUnknown: TaxVatRateDto | undefined;
            for (const vatRate of data ?? []) {
                if (vatRate.rateType === rateType.RATE_BASE) {
                    vatBasic = vatRate;
                } else if (vatRate.rateType === rateType.RATE_REDUCED) {
                    vatReduced = vatRate;
                } else if (vatRate.rateType === rateType.RATE_REDUCED2) {
                    vatReduced2 = vatRate;
                } else if (vatRate.rateType === rateType.RATE_0) {
                    vat0 = vatRate;
                } else if (vatRate.rateType === rateType.NONE) {
                    vatNone = vatRate;
                } else if (vatRate.rateType === rateType.UNKNOWN) {
                    vatUnknown = vatRate;
                }
            }
            onVatRates({ vat0, vatBasic, vatReduced, vatReduced2, vatNone, vatUnknown });
        })
        .catch(() => onVatRates(undefined));
}

export const documentFieldSxByState =
    ({ hovering, readOnly, onlyForRecogniser }: { readOnly: boolean; onlyForRecogniser: boolean; hovering: boolean }) =>
    (theme: Theme): SystemStyleObject<Theme> => {
        return {
            "& .MuiInputBase-root": {
                /*...(hovering && { border: "medium solid" }),                                                                                                                           boxSizing: "border-box",*/
                borderColor: theme.palette.primary.main,
                ...(readOnly && readOnlyInputSx),
                ...(onlyForRecogniser && { backgroundImage: onlyForRecogniserFieldBackgroundImage }),
                ...(hovering && {
                    "& fieldset": {
                        border: `3px solid ${theme.palette.primary.main}`,
                    },
                }),
            },
        };
    };

export const onlyForRecogniserFieldBackgroundImage =
    "repeating-linear-gradient(-45deg, rgb(255 0 0 / 15%) 0, rgb(255 0 0 / 15%) 5px, rgba(255,0,0,0) 10px, rgba(255,0,0,0) 15px)";

export function createValidationResult(messages: Array<CoreViolationEntry>, data: DocumentDetailFormTypes): ValidationResult {
    const validationResult: ValidationResult = {};
    const validationsByField: Partial<Record<Path<DocumentDetailFormTypes>, Array<CoreViolationEntry>>> = {};
    messages.forEach((value) => {
        if (!value.fieldName) {
            return;
        }
        const fieldNameType = value.fieldName as Path<DocumentDetailFormTypes>;
        const previousValue = validationsByField[fieldNameType] ?? [];
        validationsByField[fieldNameType] = [...previousValue, value];
    });
    for (const fieldNameWithError in validationsByField) {
        const fieldAsPath = fieldNameWithError as Path<DocumentDetailFormTypes>;
        const value = data[fieldNameWithError as keyof DocumentDetailFormTypes];
        validationResult[fieldAsPath] = {
            forValue: serialiseFormValue(value),
            messages: validationsByField[fieldAsPath] ?? [],
        };
    }
    return validationResult;
}

function serialiseFormValue(value: PathValue<DocumentDetailFormTypes, Path<DocumentDetailFormTypes>>) {
    if (!value) {
        return value;
    }
    if (isDateObject(value)) {
        return value.toJSON();
    } else if (!Array.isArray(value) && typeof value === "object" && (value as { id?: string })?.id) {
        return value.id;
    }
    return value;
}

export function splitCoreViolationsByType(violations: Array<CoreViolationEntry>): {
    errors: Array<CoreViolationEntry>;
    warnings: Array<CoreViolationEntry>;
} {
    const errors: Array<CoreViolationEntry> = [];
    const warnings: Array<CoreViolationEntry> = [];

    for (const violation of violations) {
        if (violation.errorType === errorType.ERROR) {
            errors.push(violation);
        } else if (violation.errorType === errorType.WARNING) {
            warnings.push(violation);
        }
    }

    return { errors, warnings };
}

export function validationGroupedById(violations: Array<CoreViolationEntry>): Record<number, Array<CoreViolationEntry>> {
    const result: Record<number, Array<CoreViolationEntry>> = {};
    violations.forEach((violation) => {
        const currentValidationId = violation.validationId ?? 0;
        const currentArray: Array<CoreViolationEntry> | undefined = result[currentValidationId] ?? [];
        result[currentValidationId] = [...currentArray, violation];
    });
    return result;
}

export function extractValueOrObjectId(value: unknown): unknown | undefined {
    if (!isValueDefined(value)) {
        return undefined;
    }
    if (typeof value !== "object") {
        return value;
    }
    if (isDateObjectValid(value)) {
        return dateForTaxWithTime(toDateObject(value));
    }
    const valueAsObject = value as { id: string };
    if (valueAsObject?.id) {
        return valueAsObject.id;
    }
    return undefined;
}

export function dateForVatRates(...datesByPriority: Array<DateObjectInput>): Dayjs {
    const date = datesByPriority.find((item) => item !== undefined && item !== null);
    if (!date) {
        return newDateObjectInstance();
    }
    if (typeof date === "string") {
        const m = dayjs(date);
        if (m.isValid()) {
            return m;
        }
        return newDateObjectInstance();
    }
    return date;
}

export function isDocumentStatusAtLeast(documentStatus: TaxDocumentDto["status"], status: TaxDocumentDto["status"]) {
    const statusOrder: Array<TaxDocumentDto["status"]> = [
        TaxDocumentDto["status"].INSERTED,
        TaxDocumentDto["status"].EXTRACTED,
        TaxDocumentDto["status"].AWAITING_CORRECTION,
        TaxDocumentDto["status"].UNDER_CORRECTION,
        TaxDocumentDto["status"].CONFIRMED,
        TaxDocumentDto["status"].IN_ACCOUNTING,
        TaxDocumentDto["status"].ACCOUNTING_ERROR,
    ];
    const idx = statusOrder.indexOf(status);
    return statusOrder.indexOf(documentStatus, idx) !== -1;
}
